import {
	CanvasTexture,
	Color,
	Mesh,
	MeshStandardMaterial,
	SphereGeometry
} from 'three';

import { hueToColor } from './color-utils';
import { LIGHT_HELPER_DEFAULTS } from './light-helper.types';

/**
 * Create a color wheel texture using canvas
 * The texture shows a full color wheel (hue circle)
 *
 * @param size Texture size in pixels (default: 256)
 * @returns CanvasTexture with color wheel
 */
export const createColorWheelTexture = (size: number = 256): CanvasTexture => {
	const canvas = document.createElement('canvas');
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext('2d');

	if (!ctx) {
		throw new Error('Could not get 2D context for color wheel texture');
	}

	const centerX = size / 2;
	const centerY = size / 2;
	const radius = size / 2;

	// Draw color wheel using conic gradient
	// Create radial segments for the color wheel
	const segments = 360;
	for (let i = 0; i < segments; i++) {
		const startAngle = (i / segments) * Math.PI * 2 - Math.PI / 2;
		const endAngle = ((i + 1) / segments) * Math.PI * 2 - Math.PI / 2;

		// Get color for this segment
		const hue = i / segments;
		const color = hueToColor(hue);
		const hexColor = `#${color.getHexString()}`;

		// Draw segment
		ctx.beginPath();
		ctx.moveTo(centerX, centerY);
		ctx.arc(centerX, centerY, radius, startAngle, endAngle);
		ctx.closePath();
		ctx.fillStyle = hexColor;
		ctx.fill();
	}

	// Add a white center gradient for 3D effect
	const gradient = ctx.createRadialGradient(
		centerX * 0.7,
		centerY * 0.7,
		0,
		centerX,
		centerY,
		radius
	);
	gradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
	gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.1)');
	gradient.addColorStop(1, 'rgba(0, 0, 0, 0.1)');

	ctx.beginPath();
	ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
	ctx.fillStyle = gradient;
	ctx.fill();

	const texture = new CanvasTexture(canvas);
	texture.needsUpdate = true;

	return texture;
};

/**
 * Create a color wheel material for the sphere
 *
 * @param size Texture size (default: 256)
 * @returns MeshStandardMaterial with color wheel texture
 */
export const createColorWheelMaterial = (size: number = 256): MeshStandardMaterial => {
	const texture = createColorWheelTexture(size);

	return new MeshStandardMaterial({
		map: texture,
		emissive: new Color(0x222222),
		emissiveIntensity: 0.5,
		roughness: 0.3,
		metalness: 0.1
	});
};

/**
 * Create a color ball sphere mesh
 *
 * @param radius Sphere radius
 * @param textureSize Texture resolution (default: 256)
 * @returns Mesh with color wheel material
 */
export const createColorBallMesh = (
	radius: number,
	textureSize: number = 256
): Mesh => {
	const geometry = new SphereGeometry(radius, 32, 32);
	const material = createColorWheelMaterial(textureSize);
	const mesh = new Mesh(geometry, material);
	mesh.name = 'colorBall';
	return mesh;
};

/**
 * Cache for color wheel textures to avoid recreating them
 */
const textureCache: Map<number, CanvasTexture> = new Map();

/**
 * Get or create a cached color wheel texture
 *
 * @param size Texture size
 * @returns Cached or new CanvasTexture
 */
export const getCachedColorWheelTexture = (size: number = 256): CanvasTexture => {
	let texture = textureCache.get(size);
	if (!texture) {
		texture = createColorWheelTexture(size);
		textureCache.set(size, texture);
	}
	return texture;
};

/**
 * Dispose of all cached textures
 */
export const disposeCachedTextures = (): void => {
	for (const texture of textureCache.values()) {
		texture.dispose();
	}
	textureCache.clear();
};

/**
 * Apply color wheel material to an existing sphere mesh
 *
 * @param sphere The sphere mesh to update
 * @param textureSize Texture resolution (default: 256)
 */
export const applyColorWheelToSphere = (
	sphere: Mesh,
	textureSize: number = 256
): void => {
	const texture = getCachedColorWheelTexture(textureSize);

	// Dispose of old material
	if (sphere.material) {
		const oldMaterial = sphere.material as MeshStandardMaterial;
		if (oldMaterial.map && oldMaterial.map !== texture) {
			// Only dispose if it's not our cached texture
			if (!Array.from(textureCache.values()).includes(oldMaterial.map as CanvasTexture)) {
				oldMaterial.map.dispose();
			}
		}
		oldMaterial.dispose();
	}

	// Apply new material
	sphere.material = new MeshStandardMaterial({
		map: texture,
		emissive: new Color(0x222222),
		emissiveIntensity: 0.5,
		roughness: 0.3,
		metalness: 0.1
	});
};

/**
 * Revert sphere to solid color material based on light on/off state
 *
 * @param sphere The sphere mesh to update
 * @param isLightOn Whether the light is currently on (determines yellow vs dark gray)
 */
export const revertToSolidColor = (
	sphere: Mesh,
	isLightOn: boolean
): void => {
	// Dispose of old material
	if (sphere.material) {
		const oldMaterial = sphere.material as MeshStandardMaterial;
		// Don't dispose cached textures
		if (oldMaterial.map) {
			const isCached = Array.from(textureCache.values()).includes(oldMaterial.map as CanvasTexture);
			if (!isCached) {
				oldMaterial.map.dispose();
			}
		}
		oldMaterial.dispose();
	}

	// Yellow when on, dark gray when off
	const colorHex = isLightOn
		? LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_ON
		: LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF;

	sphere.material = new MeshStandardMaterial({
		color: colorHex,
		emissive: colorHex,
		emissiveIntensity: 0.3
	});
};

